/**
 * Seleção de período dos relatórios (presets de 7/14/30 dias + intervalo
 * customizado) e os helpers de data que iam duplicados em cada tela.
 *
 * Antes cada tela de relatório redeclarava `pad`/`toYmd`/`addDays`/`daysOf` e o
 * seu próprio `resolveRange`. O que NÃO vem para cá é a tradução da seleção em
 * parâmetros de request: isso diverge por tela (o Volume Bruto sempre manda
 * `{from,to}` e projeta `to` para o futuro; os outros três mandam `undefined` no
 * preset de 30 dias para o backend aplicar a janela rolante dele).
 */

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Presets de janela rolante, na ordem em que aparecem no controle segmentado. */
export const PERIOD_PRESETS = ["7", "14", "30"] as const;

export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/**
 * Período pedido pelo usuário: um preset rolante ou um intervalo explícito.
 * O intervalo customizado só existe já completo — o controle não emite `custom`
 * enquanto as duas datas não estiverem escolhidas (mandar `to` sem `from` faz o
 * backend responder 400; ver `ReportTimeline/index.tsx`).
 */
export type PeriodSelection =
  | { kind: "preset"; preset: PeriodPreset }
  | { kind: "custom"; from: Date; to: Date };

/**
 * Dia-calendário local em `yyyy-MM-dd`.
 *
 * Getters locais de propósito: o dado da POC é dia-calendário local (UTC−3).
 * `toISOString().slice(0,10)` jogaria a data um dia para frente nas seleções da
 * noite no Brasil.
 */
export const toYmd = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Cópia de `d` deslocada em `days` dias (aceita negativo). */
export const addDays = (d: Date, days: number): Date => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

/** Meia-noite local do dia de `d`. */
export const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Quantidade de dias de um preset. */
export const daysOfPreset = (preset: PeriodPreset): number => Number(preset);

/** Atalho para montar a seleção de um preset. */
export const presetSelection = (preset: PeriodPreset): PeriodSelection => ({
  kind: "preset",
  preset,
});

/** Seleção padrão de todas as telas: janela rolante de 30 dias. */
export const DEFAULT_PERIOD_SELECTION: PeriodSelection = presetSelection("30");

/**
 * Janela da seleção como objetos `Date` (meia-noite local).
 * Preset de N dias = os N dias que terminam hoje, hoje incluído.
 */
export const selectionDates = (
  selection: PeriodSelection,
): { from: Date; to: Date } => {
  if (selection.kind === "custom") {
    return { from: startOfDay(selection.from), to: startOfDay(selection.to) };
  }
  const today = startOfDay(new Date());
  return { from: addDays(today, -(daysOfPreset(selection.preset) - 1)), to: today };
};

/** Janela da seleção em `yyyy-MM-dd`, pronta para virar query param. */
export const selectionYmd = (
  selection: PeriodSelection,
): { from: string; to: string } => {
  const { from, to } = selectionDates(selection);
  return { from: toYmd(from), to: toYmd(to) };
};

/**
 * `true` quando a janela alcança hoje. O Volume Bruto usa isto para decidir se
 * ainda faz sentido pedir dias projetados à frente: uma janela puramente
 * histórica não tem projeção.
 */
export const selectionReachesToday = (selection: PeriodSelection): boolean => {
  if (selection.kind === "preset") return true;
  return startOfDay(selection.to).getTime() >= startOfDay(new Date()).getTime();
};

/** Rótulo estável para `useLiveRefresh`/deps — muda quando a janela muda. */
export const selectionKey = (selection: PeriodSelection): string => {
  if (selection.kind === "preset") return `preset:${selection.preset}`;
  const { from, to } = selectionYmd(selection);
  return `custom:${from}:${to}`;
};

/**
 * Descreve a seleção para o log de atividade.
 *
 * Só a FORMA da janela pedida: tipo, preset e as duas datas em `yyyy-MM-dd`. Nada de objeto
 * `Date` (serializaria com fuso e hora, virando ruído) e nada além disto — o payload do log é
 * limitado aos parâmetros da ação, não ao conteúdo da tela.
 */
export const describeSelection = (
  selection: PeriodSelection,
): Record<string, unknown> => {
  if (selection.kind === "preset") {
    return { period: "preset", preset: selection.preset };
  }
  const { from, to } = selectionYmd(selection);
  return { period: "custom", from, to };
};

/**
 * Normaliza o valor do `<Calendar selectionMode="range">`, que devolve
 * `[Date, null]` no meio da seleção. Só devolve um par completo — e já ordenado,
 * porque o PrimeReact aceita clicar a data final antes da inicial.
 */
export const completeRange = (
  value: (Date | null)[] | Date | null | undefined,
): { from: Date; to: Date } | null => {
  if (!Array.isArray(value)) return null;
  const [a, b] = value;
  if (!(a instanceof Date) || !(b instanceof Date)) return null;
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return a.getTime() <= b.getTime()
    ? { from: startOfDay(a), to: startOfDay(b) }
    : { from: startOfDay(b), to: startOfDay(a) };
};
